// App Check SANS risque de bloquer l'app (C1, 25/09/2026), et SANS retarder
// l'écran de scan (26/09/2026, ordre CTO : « jamais plus de ~0,5 s »).
//
// LE PIÈGE (25/09). Le SDK App Check (0.8) charge lui-même le script reCAPTCHA
// Enterprise avec un seul `onload`, sans `onerror`. Si ce script est bloqué
// (bloqueur de contenu, réseau filtrant, navigateur intégré…), sa promesse
// d'initialisation ne se résout JAMAIS : Auth et Firestore attendent leur jeton
// App Check indéfiniment, aucune requête ne part, et l'enfant voit un écran
// vide. Parade : on charge le script NOUS-MÊMES (délai, gestion d'échec) et on
// n'initialise App Check que s'il est arrivé.
//
// LE COÛT (26/09). Une fois App Check initialisé, chaque requête attend son
// jeton ; le premier jeton d'un téléphone demande reCAPTCHA (0,3 à 1,5 s).
// L'app attendait jusqu'à 2 s AVANT de se monter. Désormais :
//  - l'app se monte TOUT DE SUITE, le script reCAPTCHA arrive en arrière-plan ;
//  - si ce téléphone a déjà un jeton valide (retenu dans le stockage du SDK,
//    durée de vie réglée à 1 jour côté projet), App Check démarre dès le
//    script arrivé : ses requêtes partent avec ce jeton, sans appel reCAPTCHA ;
//  - sinon (1re visite, jeton expiré), App Check ne démarre qu'une fois l'écran
//    de scan affiché (signalerEcranPret), 3 s au plus : l'écran ne l'attend
//    pas, ses requêtes partent sans jeton (acceptées : Firestore est en
//    SURVEILLANCE), et le jeton est prêt pour la suite et la prochaine visite.
// ⚠ Le jour où Firestore passe en mode IMPOSÉ, ces premières requêtes sans
// jeton seraient refusées : il faudra alors démarrer App Check avant l'écran
// et accepter l'attente de reCAPTCHA à la 1re visite du jour.
import { initializeAppCheck, onTokenChanged, ReCaptchaEnterpriseProvider } from "firebase/app-check";

const SCRIPT_RECAPTCHA = "https://www.google.com/recaptcha/enterprise.js";
export const ECRAN_PRET = "animax:ecran-pret";
const CLE_EXPIRATION = "animax:appcheck:expire";
const MARGE_MS = 2 * 60 * 1000; // un jeton qui expire dans 2 min ne compte pas
const DIFFERE_MAX_MS = 3000;

/** Charge un script ; rend true s'il est arrivé avant le délai, false sinon. */
export function chargerScript(url, delaiMs) {
  return new Promise((ok) => {
    const s = document.createElement("script");
    let fini = false;
    const conclure = (v) => {
      if (fini) return;
      fini = true;
      clearTimeout(minuteur);
      ok(v);
    };
    const minuteur = setTimeout(() => conclure(false), delaiMs);
    s.src = url;
    s.async = true;
    s.onload = () => conclure(true);
    s.onerror = () => conclure(false);
    document.head.appendChild(s);
  });
}

/** Échéance (ms) d'un jeton App Check : c'est un JWT, on lit son `exp`. 0 si illisible. */
export function expirationDuJeton(jwt) {
  try {
    const b64 = String(jwt).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const exp = JSON.parse(atob(b64)).exp;
    return Number.isFinite(exp) ? exp * 1000 : 0;
  } catch {
    return 0;
  }
}

/** Ce téléphone a-t-il un jeton encore valable (d'après l'échéance retenue) ? */
export function jetonEncoreValide(expireLe, maintenant = Date.now()) {
  return Number(expireLe) - MARGE_MS > maintenant;
}

const lire = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const ecrire = (k, v) => { try { localStorage.setItem(k, v); } catch { /* stockage indisponible */ } };

/** L'écran courant a ses données : App Check peut démarrer sans le gêner. */
export function signalerEcranPret() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(ECRAN_PRET));
}

function attendreEcranPret(maxMs) {
  return new Promise((ok) => {
    const fin = () => {
      clearTimeout(minuteur);
      window.removeEventListener(ECRAN_PRET, fin);
      ok();
    };
    const minuteur = setTimeout(fin, maxMs);
    window.addEventListener(ECRAN_PRET, fin);
  });
}

/**
 * Démarre App Check en arrière-plan (voir l'en-tête). Ne bloque jamais rien :
 * rend une promesse (true si App Check tourne) que personne n'a à attendre.
 */
export async function demarrerAppCheckEnFond(app, cleSite, { delaiScriptMs = 10000 } = {}) {
  if (!cleSite || typeof document === "undefined") return false;
  const moment = jetonEncoreValide(lire(CLE_EXPIRATION))
    ? Promise.resolve()
    : attendreEcranPret(DIFFERE_MAX_MS);
  const script = window.grecaptcha?.enterprise
    ? Promise.resolve(true)
    : chargerScript(SCRIPT_RECAPTCHA, delaiScriptMs);
  const [arrive] = await Promise.all([script, moment]);
  if (!arrive || !window.grecaptcha?.enterprise) {
    console.warn("[app-check] reCAPTCHA injoignable : l'app continue sans App Check.");
    return false;
  }
  try {
    const appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(cleSite),
      isTokenAutoRefreshEnabled: true,
    });
    onTokenChanged(appCheck, (r) => ecrire(CLE_EXPIRATION, String(expirationDuJeton(r.token))));
    return true;
  } catch (e) {
    console.warn("[app-check] initialisation impossible :", e);
    return false;
  }
}
